import React from 'react'
import { formatPhone } from '@@partner:helpers/util'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { useFetchAllSupervisors } from '@@partner:graphql/query/employees/allSupervisors.gql'

import { Link, generatePath } from 'react-router-dom'
import { Table, Pagination, Tag } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import { EditButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { ROUTE_USERS_MANAGEMENT_EDIT } from '@@partner:config/routes'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

export interface SupervisorsTableProps {
  header?: string
}

export default function SupervisorsTable ({ header }: SupervisorsTableProps) {
  const { page, tableSort, handlePageSelect, handleSortColumn } =
    useGridHelper()

  const { data, loading, error } = useFetchAllSupervisors({
    pager: { page },
    sort: {
      direction: tableSort.direction,
      fieldName: tableSort.fieldName
    }
  })

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки супервайзеров"
      />
    )
  }

  const { edges: supervisors = [], pageInfo } = data?.supervisors ?? {}

  return (
    <>
      {header && <h4 className="u-form-heading">{header}</h4>}
      <Table
        data={supervisors}
        autoHeight
        wordWrap
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        loading={loading}
        renderLoading={() =>
          loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
        }
        affixHeader
      >
        <Column sortable flexGrow={1} verticalAlign="middle">
          <HeaderCell>ИД</HeaderCell>
          <Cell dataKey="id" />
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>ФИО</HeaderCell>
          <Cell dataKey="first_name">
            {(supervisor) => <span>{supervisor.fullName}</span>}
          </Cell>
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>Телефон</HeaderCell>
          <Cell dataKey="phone">
            {(supervisor) => (
              <Tag color="blue">{formatPhone(supervisor.phone)}</Tag>
            )}
          </Cell>
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>E-mail</HeaderCell>
          <Cell dataKey="email">
            {(supervisor) => <Tag color="blue">{supervisor.email}</Tag>}
          </Cell>
        </Column>

        <Column sortable flexGrow={1} verticalAlign="middle">
          <HeaderCell>Статус</HeaderCell>
          <Cell dataKey="status">
            {(supervisor) => <CommonStatusLabel status={supervisor.status} />}
          </Cell>
        </Column>

        <Column flexGrow={2} fixed="right" verticalAlign="middle">
          <HeaderCell>Управление</HeaderCell>
          <Cell>
            {(supervisor) => (
              <EditButton
                color="cyan"
                as={Link}
                to={generatePath(ROUTE_USERS_MANAGEMENT_EDIT, {
                  type: 'supervisors',
                  id: supervisor.id
                })}
                size="xs"
              />
            )}
          </Cell>
        </Column>
      </Table>
      <Pagination
        last
        first
        next
        prev
        ellipsis
        maxButtons={10}
        activePage={page}
        pages={pageInfo?.lastPage}
        onSelect={handlePageSelect}
      />
    </>
  )
}
